import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { Globe, MapPin, Users, Cloud, Clock, Newspaper, Eye, Lock, Unlock, Settings, AlertTriangle, Shield } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { convertCityToCountry } from "@/utils/cityToCountry";
import { useToast } from "@/hooks/use-toast";
import CompanyCountryDetailDialog from "@/components/dashboard/CompanyCountryDetailDialog";
import CSVDestinationImport from "@/components/dashboard/CSVDestinationImport";
import CSVDestinationExport from "@/components/dashboard/CSVDestinationExport";
import ImportExportHistory from "@/components/dashboard/ImportExportHistory";

interface Company {
  id: string;
  name: string;
  sector: string;
  employee_count: number;
}

interface CountryData {
  name: string;
  employeeCount: number;
  employees: Array<{
    id: string;
    name: string;
    position: string | null;
  }>;
  weather?: {
    temperature: string;
    description: string;
    icon: string;
    humidity?: number;
    wind?: string;
    location?: string;
  };
  timezone?: {
    time: string;
    timezone: string;
    offset?: string;
  };
  news?: Array<{
    title: string;
    description: string;
    url: string;
    publishedAt: string;
    source?: string;
  }>;
}

interface Country {
  id: string;
  name: string;
  city?: string | null;
  flag_url: string | null;
  banner_url: string | null;
  tagline: string | null;
  tourist_stay_default: string | null;
  remote_work_visa_option: string | null;
  key_requirements: string | null;
  tax_residency_trigger: string | null;
  ease_compliance: number | null;
  ease_1_5_months: number | null;
  cost_of_living: number | null;
  attractiveness_rating: number | null;
  total_score: number | null;
  employee_notes: string | null;
  legal_compliance_risks: string | null;
  hr_guidelines: string | null;
  company_caps_observed: string | null;
  renest_notes: string | null;
  is_active: boolean | null;
  risk_level: string | null;
}

interface CompanyCountry {
  country_id: string;
  is_open: boolean;
  notes: string | null;
  risk_level_override: string | null;
}

interface CountriesTabProps {
  company: Company | null;
}

const CountriesTab = ({ company }: CountriesTabProps) => {
  const [employeeCountries, setEmployeeCountries] = useState<CountryData[]>([]);
  const [allCountries, setAllCountries] = useState<Country[]>([]);
  const [companyCountries, setCompanyCountries] = useState<Map<string, CompanyCountry>>(new Map());
  const [loading, setLoading] = useState(true);
  const [selectedCountry, setSelectedCountry] = useState<CountryData | null>(null);
  const [selectedSystemCountry, setSelectedSystemCountry] = useState<Country | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [loadingCountryDetails, setLoadingCountryDetails] = useState(false);
  const { toast } = useToast();

  // Update time every second for selected country
  useEffect(() => {
    const tz = selectedCountry?.timezone?.timezone;
    const offset = selectedCountry?.timezone?.offset;
    if (!tz && !offset) return;

    const formatter = tz && tz.includes('/')
      ? new Intl.DateTimeFormat('fr-FR', {
          hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: tz,
        })
      : null;

    const tick = () => {
      let timeStr = '';
      if (formatter) {
        timeStr = formatter.format(new Date());
      } else if (offset) {
        const match = offset.match(/UTC([+-]\d+(?:\.\d+)?)/);
        const hours = match ? parseFloat(match[1]) : 0;
        const target = new Date(Date.now() + hours * 3600 * 1000);
        timeStr = target.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      } else {
        timeStr = new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      }

      setSelectedCountry(prev => (prev ? { ...prev, timezone: { ...prev.timezone!, time: timeStr } } : prev));
    };

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [selectedCountry?.timezone?.timezone, selectedCountry?.timezone?.offset]);

  useEffect(() => {
    if (company) {
      fetchAllData();
    }
  }, [company]);

  const fetchAllData = async () => {
    if (!company) return;

    await Promise.all([
      fetchEmployeeCountriesData(),
      fetchSystemCountriesData()
    ]);
    
    setLoading(false);
  };

  const fetchEmployeeCountriesData = async () => {
    if (!company) return;

    try {
      // Fetch all employees with their residence countries
      const { data: employees } = await supabase
        .from('employees')
        .select(`
          id,
          first_name,
          last_name,
          position,
          residence_country,
          company_id
        `)
        .eq('company_id', company.id)
        .not('residence_country', 'is', null);

      // Fetch approved remote work requests
      const { data: allRemoteRequests } = await supabase
        .rpc('get_company_remote_work_requests');

      const remoteRequests = (allRemoteRequests || []).filter(
        req => req.status === 'approved'
      ).map(async (request) => {
        const { data: empData } = await supabase
          .from('employees')
          .select('id, first_name, last_name, position')
          .eq('id', request.employee_id)
          .single();

        return {
          ...request,
          employees: empData || {
            id: request.employee_id,
            first_name: 'N/A',
            last_name: 'N/A',
            position: null,
            company_id: company.id
          }
        };
      });

      const resolvedRemoteRequests = await Promise.all(remoteRequests);

      // Group by countries
      const countryMap = new Map<string, CountryData>();

      // Process employees by residence countries
      if (employees) {
        for (const employee of employees) {
          const locationName = employee.residence_country || 'Pays non spécifié';
          const countryName = await convertCityToCountry(locationName);
          
          if (!countryMap.has(countryName)) {
            countryMap.set(countryName, {
              name: countryName,
              employeeCount: 0,
              employees: []
            });
          }

          const country = countryMap.get(countryName)!;
          const employeeExists = country.employees.some(emp => emp.id === employee.id);
          
          if (!employeeExists) {
            country.employeeCount++;
            country.employees.push({
              id: employee.id,
              name: `${employee.first_name} ${employee.last_name}`,
              position: employee.position
            });
          }
        }
      }

      // Process approved remote requests
      if (resolvedRemoteRequests) {
        for (const request of resolvedRemoteRequests) {
          const locationName = request.destination_country || 'Pays non spécifié';
          const countryName = await convertCityToCountry(locationName);
          
          if (!countryMap.has(countryName)) {
            countryMap.set(countryName, {
              name: countryName,
              employeeCount: 0,
              employees: []
            });
          }

          const country = countryMap.get(countryName)!;
          const employeeExists = country.employees.some(emp => emp.id === request.employee_id);
          
          if (!employeeExists) {
            country.employeeCount++;
            country.employees.push({
              id: request.employee_id,
              name: `${request.employees.first_name} ${request.employees.last_name}`,
              position: request.employees.position
            });
          }
        }
      }

      const countriesArray = Array.from(countryMap.values());
      setEmployeeCountries(countriesArray);
    } catch (error) {
      console.error('Error fetching employee countries:', error);
    }
  };

  const fetchSystemCountriesData = async () => {
    if (!company) return;

    try {
      // Fetch all active countries from the system
      const { data: countriesData, error: countriesError } = await supabase
        .from('countries')
        .select('*')
        .eq('is_active', true)
        .order('name');

      if (countriesError) throw countriesError;

      // Fetch company-specific country settings (including risk_level_override)
      const { data: companyCountriesData, error: companyCountriesError } = await supabase
        .from('company_countries')
        .select('country_id, is_open, notes, risk_level_override')
        .eq('company_id', company.id);

      if (companyCountriesError) throw companyCountriesError;

      setAllCountries(countriesData || []);
      
      // Create a map for quick lookup
      const ccMap = new Map<string, CompanyCountry>();
      (companyCountriesData || []).forEach(cc => {
        ccMap.set(cc.country_id, cc);
      });
      setCompanyCountries(ccMap);

      // Auto-open countries where employees are located
      await autoOpenEmployeeCountries(countriesData || [], ccMap);
    } catch (error) {
      console.error('Error fetching system countries:', error);
    }
  };

  const autoOpenEmployeeCountries = async (countries: Country[], currentCompanyCountries: Map<string, CompanyCountry>) => {
    if (!company) return;

    const employeeCountryNames = employeeCountries.map(ec => ec.name.toLowerCase());
    const countriesToOpen = countries.filter(c => 
      employeeCountryNames.includes(c.name.toLowerCase()) &&
      !currentCompanyCountries.get(c.id)?.is_open
    );

    for (const country of countriesToOpen) {
      try {
        const existing = currentCompanyCountries.get(country.id);
        
        if (existing) {
          await supabase
            .from('company_countries')
            .update({ 
              is_open: true,
              updated_at: new Date().toISOString()
            })
            .eq('company_id', company.id)
            .eq('country_id', country.id);
        } else {
          await supabase
            .from('company_countries')
            .insert({
              company_id: company.id,
              country_id: country.id,
              is_open: true
            });
        }

        currentCompanyCountries.set(country.id, {
          country_id: country.id,
          is_open: true,
          notes: existing?.notes || null,
          risk_level_override: existing?.risk_level_override || null
        });
      } catch (error) {
        console.error(`Error opening country ${country.name}:`, error);
      }
    }

    setCompanyCountries(new Map(currentCompanyCountries));
  };

  const fetchCountryDetails = async (countryName: string) => {
    setLoadingCountryDetails(true);
    setSelectedCountry(null);
    
    try {
      const [weatherRes, timezoneRes, newsRes] = await Promise.all([
        supabase.functions.invoke('get-weather', { 
          body: { country: countryName } 
        }),
        supabase.functions.invoke('get-timezone', { 
          body: { country: countryName } 
        }),
        supabase.functions.invoke('get-news', { 
          body: { country: countryName } 
        })
      ]);

      const country = employeeCountries.find(c => c.name === countryName);
      if (country) {
        const updatedCountry = {
          ...country,
          weather: weatherRes.data?.weather || {
            temperature: "22°C",
            description: `Données météo non disponibles pour ${countryName}`,
            icon: "🌤️",
            humidity: 50,
            wind: "10 km/h",
            location: countryName
          },
          timezone: timezoneRes.data?.timezone || {
            time: new Date().toLocaleTimeString('fr-FR'),
            timezone: "CET",
            offset: "UTC+1"
          },
          news: newsRes.data?.news || [
            {
              title: `Actualités de ${countryName}`,
              description: "Les actualités ne sont pas disponibles actuellement...",
              url: "#",
              publishedAt: new Date().toISOString(),
              source: "Local"
            }
          ]
        };
        
        setSelectedCountry(updatedCountry);
      }
    } catch (error) {
      console.error('Error fetching country details:', error);
      
      const country = employeeCountries.find(c => c.name === countryName);
      if (country) {
        setSelectedCountry({
          ...country,
          weather: {
            temperature: "N/A",
            description: `Erreur de chargement`,
            icon: "🌤️",
            humidity: 0,
            wind: "N/A",
            location: countryName
          },
          timezone: {
            time: new Date().toLocaleTimeString('fr-FR'),
            timezone: "N/A",
            offset: "N/A"
          },
          news: []
        });
      }
    } finally {
      setLoadingCountryDetails(false);
    }
  };

  const handleCountryClick = (countryName: string) => {
    setIsDialogOpen(true);
    setTimeout(() => {
      fetchCountryDetails(countryName);
    }, 100);
  };

  const toggleCountryAccess = async (countryId: string, currentStatus: boolean) => {
    if (!company) return;

    try {
      const newStatus = !currentStatus;
      const companyCountry = companyCountries.get(countryId);
      
      if (companyCountry) {
        const { error } = await supabase
          .from('company_countries')
          .update({ 
            is_open: newStatus,
            updated_at: new Date().toISOString()
          })
          .eq('company_id', company.id)
          .eq('country_id', countryId);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('company_countries')
          .insert({
            company_id: company.id,
            country_id: countryId,
            is_open: newStatus
          });

        if (error) throw error;
      }

      const updatedMap = new Map(companyCountries);
      updatedMap.set(countryId, {
        country_id: countryId,
        is_open: newStatus,
        notes: companyCountry?.notes || null,
        risk_level_override: companyCountry?.risk_level_override || null
      });
      setCompanyCountries(updatedMap);

      toast({
        title: newStatus ? "Pays ouvert" : "Pays fermé",
        description: newStatus 
          ? "Ce pays est maintenant accessible à vos employés"
          : "Ce pays n'est plus accessible à vos employés"
      });
    } catch (error) {
      console.error('Error toggling country access:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de modifier l'accès au pays"
      });
    }
  };

  const updateCountryRiskLevel = async (countryId: string, riskLevel: string) => {
    if (!company) return;
    
    try {
      const companyCountry = companyCountries.get(countryId);
      
      if (companyCountry) {
        // Update existing company_countries record
        const { error } = await supabase
          .from('company_countries')
          .update({ 
            risk_level_override: riskLevel,
            updated_at: new Date().toISOString()
          })
          .eq('company_id', company.id)
          .eq('country_id', countryId);

        if (error) throw error;
      } else {
        // Create new company_countries record with risk level
        const { error } = await supabase
          .from('company_countries')
          .insert({
            company_id: company.id,
            country_id: countryId,
            is_open: false,
            risk_level_override: riskLevel
          });

        if (error) throw error;
      }

      // Update local state
      const updatedMap = new Map(companyCountries);
      updatedMap.set(countryId, {
        country_id: countryId,
        is_open: companyCountry?.is_open || false,
        notes: companyCountry?.notes || null,
        risk_level_override: riskLevel
      });
      setCompanyCountries(updatedMap);

      toast({
        title: "Risk level updated",
        description: `Country risk level set to ${riskLevel}`,
      });
    } catch (error) {
      console.error('Error updating risk level:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update country risk level"
      });
    }
  };

  const getRiskBadgeClass = (level: string | null) => {
    switch (level) {
      case 'high':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      default:
        return 'bg-green-100 text-green-700 border-green-200';
    }
  };

  const isCountryOpen = (countryId: string): boolean => {
    return companyCountries.get(countryId)?.is_open || false;
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Card>
          <CardContent className="p-6">
            <div className="animate-pulse space-y-4">
              <div className="h-8 bg-muted rounded w-1/4"></div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-32 bg-muted rounded"></div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <>
      <Tabs defaultValue="distribution" className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="distribution" className="flex items-center gap-2">
            <Globe className="h-4 w-4" />
            Geographic Distribution
          </TabsTrigger>
          <TabsTrigger value="management" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Access & Risk Management
          </TabsTrigger>
        </TabsList>

        <TabsContent value="distribution">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5" />
                Geographic Distribution
              </CardTitle>
              <CardDescription>
                View where your employees are located around the world
              </CardDescription>
            </CardHeader>
            <CardContent>
              {employeeCountries.length === 0 ? (
                <div className="text-center py-8">
                  <MapPin className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">
                    Aucun collaborateur en télétravail actuellement
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {employeeCountries.map((country) => (
                    <Card 
                      key={country.name} 
                      className="cursor-pointer hover:shadow-md transition-shadow"
                      onClick={() => handleCountryClick(country.name)}
                    >
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-lg">{country.name}</CardTitle>
                          <Badge variant="secondary">
                            {country.employeeCount} {country.employeeCount > 1 ? 'employés' : 'employé'}
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Users className="h-4 w-4" />
                            <span>{country.employees.length > 0 ? country.employees.slice(0, 2).map(emp => emp.name).join(', ') : 'Aucun employé'}</span>
                          </div>
                          {country.employees.length > 2 && (
                            <p className="text-xs text-muted-foreground">
                              et {country.employees.length - 2} autre(s)...
                            </p>
                          )}
                          <Button variant="outline" size="sm" className="w-full mt-3">
                            <Eye className="h-3 w-3 mr-2" />
                            Voir les détails
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="management">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="h-5 w-5" />
                    Country Access & Risk Management
                  </CardTitle>
                  <CardDescription>
                    Open/close country access and set risk levels for your employees
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  {company && (
                    <>
                      <CSVDestinationExport companyId={company.id} />
                      <CSVDestinationImport 
                        companyId={company.id} 
                        onImportComplete={fetchSystemCountriesData}
                      />
                    </>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {allCountries.length === 0 ? (
                <div className="text-center py-8">
                  <MapPin className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">
                    Aucun pays disponible dans le système
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {allCountries.map((country) => {
                    const isOpen = isCountryOpen(country.id);
                    return (
                      <Card 
                        key={country.id} 
                        className={`transition-all ${
                          isOpen 
                            ? 'border-primary/50' 
                            : 'border-muted'
                        }`}
                      >
                        <CardHeader className="pb-3">
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-lg flex items-center gap-2">
                              {country.flag_url && (
                                <img 
                                  src={country.flag_url} 
                                  alt={`${country.name} flag`}
                                  className="w-6 h-4 object-cover rounded"
                                />
                              )}
                              {country.name}
                            </CardTitle>
                            <Badge variant={isOpen ? "default" : "secondary"}>
                              {isOpen ? (
                                <span className="flex items-center gap-1">
                                  <Unlock className="h-3 w-3" />
                                  Ouvert
                                </span>
                              ) : (
                                <span className="flex items-center gap-1">
                                  <Lock className="h-3 w-3" />
                                  Fermé
                                </span>
                              )}
                            </Badge>
                          </div>
                          {country.tagline && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {country.tagline}
                            </p>
                          )}
                        </CardHeader>
                        <CardContent className="space-y-3">
                          {/* Risk Level Selector */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 text-sm">
                              <Shield className="h-4 w-4 text-muted-foreground" />
                              <span className="text-muted-foreground">Risk Level:</span>
                            </div>
                            <Select
                              value={companyCountries.get(country.id)?.risk_level_override || country.risk_level || 'low'}
                              onValueChange={(value) => updateCountryRiskLevel(country.id, value)}
                            >
                              <SelectTrigger className="w-[120px] h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="low">
                                  <span className="flex items-center gap-2">
                                    <div className="w-2 h-2 rounded-full bg-green-500" />
                                    Low
                                  </span>
                                </SelectItem>
                                <SelectItem value="medium">
                                  <span className="flex items-center gap-2">
                                    <div className="w-2 h-2 rounded-full bg-yellow-500" />
                                    Medium
                                  </span>
                                </SelectItem>
                                <SelectItem value="high">
                                  <span className="flex items-center gap-2">
                                    <div className="w-2 h-2 rounded-full bg-red-500" />
                                    High
                                  </span>
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          
                          {country.total_score && (
                            <div className="text-sm">
                              <span className="text-muted-foreground">Score: </span>
                              <span className="font-semibold">{country.total_score}/20</span>
                            </div>
                          )}
                          <div className="flex gap-2">
                            <Button 
                              variant="outline" 
                              size="sm" 
                              className="flex-1"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedSystemCountry(country);
                                setIsDetailOpen(true);
                              }}
                            >
                              Details
                            </Button>
                            <Button 
                              variant={isOpen ? "destructive" : "default"}
                              size="sm" 
                              className="flex-1"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleCountryAccess(country.id, isOpen);
                              }}
                            >
                              {isOpen ? "Close" : "Open"}
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
              
              {/* Import/Export History */}
              {company && (
                <div className="mt-8">
                  <ImportExportHistory companyId={company.id} />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Employee Country Details Dialog (with weather, time, news) */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5" />
              {selectedCountry?.name}
            </DialogTitle>
          </DialogHeader>

          {loadingCountryDetails ? (
            <div className="space-y-4 p-4">
              <div className="animate-pulse space-y-4">
                <div className="h-20 bg-muted rounded"></div>
                <div className="h-40 bg-muted rounded"></div>
                <div className="h-60 bg-muted rounded"></div>
              </div>
            </div>
          ) : selectedCountry && (
            <div className="space-y-6">
              {/* Weather and Time */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Cloud className="h-4 w-4" />
                      Météo actuelle
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{selectedCountry.weather?.icon}</span>
                      <div>
                        <p className="text-xl font-semibold">{selectedCountry.weather?.temperature}</p>
                        <p className="text-sm text-muted-foreground">{selectedCountry.weather?.description}</p>
                        {selectedCountry.weather?.humidity && (
                          <p className="text-xs text-muted-foreground">Humidité: {selectedCountry.weather.humidity}%</p>
                        )}
                        {selectedCountry.weather?.wind && (
                          <p className="text-xs text-muted-foreground">Vent: {selectedCountry.weather.wind}</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Heure locale
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-semibold">{selectedCountry.timezone?.time}</p>
                    <p className="text-sm text-muted-foreground">{selectedCountry.timezone?.timezone}</p>
                    {selectedCountry.timezone?.offset && (
                      <p className="text-xs text-muted-foreground">{selectedCountry.timezone.offset}</p>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Employees List */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Collaborateurs ({selectedCountry.employeeCount})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {selectedCountry.employees.map(employee => (
                      <div key={employee.id} className="flex justify-between items-center p-2 rounded hover:bg-muted/50">
                        <span className="font-medium">{employee.name}</span>
                        <span className="text-sm text-muted-foreground">{employee.position || 'Poste non spécifié'}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* News */}
              {selectedCountry.news && selectedCountry.news.length > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Newspaper className="h-4 w-4" />
                      Actualités locales
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {selectedCountry.news.slice(0, 3).map((article, idx) => (
                        <div key={idx} className="space-y-1">
                          <a 
                            href={article.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="font-medium hover:underline text-sm"
                          >
                            {article.title}
                          </a>
                          <p className="text-xs text-muted-foreground line-clamp-2">
                            {article.description}
                          </p>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            {article.source && <span>{article.source}</span>}
                            <span>•</span>
                            <span>{new Date(article.publishedAt).toLocaleDateString('fr-FR')}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* System Country Details Dialog */}
      <CompanyCountryDetailDialog
        country={selectedSystemCountry}
        open={isDetailOpen}
        onClose={() => {
          setIsDetailOpen(false);
          setSelectedSystemCountry(null);
        }}
      />
    </>
  );
};

export default CountriesTab;
